import React from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { useTranslation } from "@/lib/i18n";

function fmtAxis(n) {
  // Phase 6.AH null-truthfulness — never call `.toFixed` on a non-finite
  // number. Recharts can hand us undefined/null when a tick falls on a
  // data gap, and the old `(n || 0)` coercion would mask malformed input.
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  const v = Math.abs(num);
  if (v >= 1e12) return `${(num / 1e12).toFixed(1)}T`;
  if (v >= 1e9) return `${(num / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return `${num}`;
}

function Tip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg border border-border/70 bg-popover/95 px-3 py-2 shadow-[var(--app-shadow-elev-1)]">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="font-mono tabular text-sm">${fmtAxis(payload[0]?.value)}</div>
    </div>
  );
}

export default function GrowthChart({ title, data, color = "#3182CE", testId }) {
  const { t } = useTranslation();
  // Phase 6.AH null-truthfulness — defensively coerce to an array AND
  // filter out malformed rows. Backend should already do this via the
  // response contract, but a single bad row from the FMP raw bundle must
  // never crash recharts.
  const fmtData = (Array.isArray(data) ? data : [])
    .filter((d) => d && (d.year != null || d.value != null))
    .map((d) => ({
      year: d.year != null ? String(d.year) : "",
      value: Number.isFinite(Number(d.value)) ? Number(d.value) : null,
    }));
  return (
    <div className="rounded-xl border border-border/70 bg-card/60 p-3 sm:p-4 shadow-[var(--app-shadow-elev-1)]" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-muted-foreground">{t("analyze.chart.last_5_years")}</div>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={fmtData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#242530" strokeOpacity={0.5} vertical={false} />
          <XAxis dataKey="year" tick={{ fill: "#7D8592", fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={fmtAxis} tick={{ fill: "#7D8592", fontSize: 11 }} axisLine={false} tickLine={false} width={40} />
          <Tooltip content={<Tip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
          <Bar dataKey="value" radius={[6, 6, 0, 0]} fill={color} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
